import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { trackPageView } from '@/lib/tracking/tracking';
import { trackAdView } from '@/lib/tracking/objectTracking';
import { fetchObjectRecommendations, populateObject, populateObjectPriceInfo } from '@/ducks/object/objectActions';
import { FhhBreadcrumbs } from '@/components/breadcrumbs/Breadcrumbs';
import { publishBannerEvent } from '@/lib/helpers/banner';

import { ImageList as ObjectImages } from './components/imageList/ImageList';
import { FavoriteButton as ObjectFavoriteButton } from './components/favoriteButton/FavoriteButton';
import { ShareButtons as ObjectShareButtons } from './components/shareButtons/ShareButtons';
import { Pricing as ObjectPricing } from './components/pricing/Pricing';
import { Calendar as ObjectCalendar } from './components/calendar/Calendar';
import { Info as ObjectInfo } from './components/info/Info';
import { InfoList as ObjectInfoList } from './components/infoList/InfoList';
import { Profile as ObjectProfile } from './components/profile/Profile';
import { LocationMap as ObjectLocation } from './components/locationMap/LocationMap';
import { Footer as ObjectFooter } from './components/footer/Footer';
import { RecommendationList as ObjectRecommendations } from './components/recommendationList/RecommendationList';
import { Links as ObjectLinks } from './components/links/Links';
import { Rating as ObjectRating } from './components/rating/Rating';
import { RatingDesktop as ObjectRatingDesktop } from './components/ratingDesktop/RatingDesktop';
import { Administration as ObjectAdministration } from './components/administration/Administration';
import { Description as ObjectDescription } from './components/description/Description';
import { DigitalContract as ObjectDigitalContract } from './components/digitalContract/DigitalContract';
import { AreaProfile as ObjectAreaProfile } from './components/areaProfile/AreaProfile';
import { Panel as ObjectPanel } from './components/panel/Panel';
import { Status as ObjectStatus } from './components/status/Status';
import { PreviewBottomSheet } from './components/previewBottomSheet/PreviewBottomSheet';

export const ObjectPage = ({ deviceType, loginCookieCsrfToken, loginId, unleash }) => {
    const dispatch = useDispatch();
    const router = useRouter();
    const finnCode = router.query.finnkode;
    const objectData = useSelector((state) => state.objectData);
    const {
        adId,
        areaDescription,
        description,
        facilities,
        heading,
        images,
        isPreview: objectDataIsPreview,
        isPrivate,
        location,
        modified,
        moreInfo,
        poiNearBy,
        priceInfo,
        pricing,
        recommendationData,
        residenceDescription,
        roomsDescription,
        status,
        user,
    } = objectData;
    const filters = useSelector((state) => state.results.filters);
    const [initialBannerEventPublished, setInitialBannerEventPublished] = useState(false);
    const isMobile = Boolean(deviceType.isProbablyMobile);
    const isPreview = Boolean(objectDataIsPreview);
    const isLoggedIn = loginId !== null;
    const ratingRef = useRef(null);
    const objectPricingColumnData = pricing?.year ? [{ description: 'Åremål/langtidsleie', value: 'Kontakt utleier' }] : [];
    let newPricing = { ...pricing };
    delete newPricing.year;

    useEffect(() => {
        if (isPreview) {
            return;
        }

        // Pulse tracker id: FHH_pv02 (see tracking section in README.md)
        trackPageView({
            type: 'ClassifiedAd',
            contentId: finnCode,
            externalPartners: [
                {
                    brandName: objectData.isImport ? objectData.owner.name : undefined,
                },
            ],
        });
        trackAdView({ adId: adId || finnCode });
        window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    }, [finnCode, isPreview]);

    useEffect(() => {
        if (isPreview || !adId || initialBannerEventPublished) {
            return;
        }

        publishBannerEvent();
        setInitialBannerEventPublished(true);
    }, [adId, initialBannerEventPublished, isPreview]);

    useEffect(() => {
        if (isPreview || !adId) {
            return;
        }

        dispatch(populateObjectPriceInfo(adId));
        dispatch(fetchObjectRecommendations(adId));
    }, [adId, isPreview]);

    useEffect(() => {
        if (isPreview || adId || !finnCode) {
            return;
        }

        dispatch(populateObject(finnCode));
    }, [adId, finnCode, isPreview]);

    if (!adId) {
        return <div data-testid="objectPage" />;
    }

    const renderProfileAndCalendar = () => (
        <div className="space-y-16">
            <div className="p-16 border rounded-8 text-center">
                <ObjectProfile isLoggedIn={isLoggedIn} />
                <ObjectCalendar
                    deviceType={deviceType}
                    filters={filters}
                    loginCookieCsrfToken={loginCookieCsrfToken}
                    isLoggedIn={isLoggedIn}
                    unleash={unleash}
                    isPreview={isPreview}
                />
            </div>
            {!isPreview && <ObjectRatingDesktop adId={adId} isMobile={isMobile} ratingRef={ratingRef} />}
            {isPrivate && (
                <ObjectPanel title="Trenger du en kontrakt?" info bleed>
                    <ObjectDigitalContract adId={adId} isPreview={isPreview} />
                </ObjectPanel>
            )}
            <ObjectLinks links={moreInfo} />
        </div>
    );

    return (
        <div data-testid="objectPage">
            <FhhBreadcrumbs className="mx-0 sm:mx-16 pt-8" isPreview={isPreview} />
            <ObjectAdministration adId={adId} isPreview={isPreview} user={user} />
            <div className="grid md:grid-cols-3 gap-16 pb-32">
                {heading && images && <ObjectImages adId={adId} heading={heading} images={images} />}
                <article className="md:col-span-2 space-y-24">
                    <div className="mx-0 sm:mx-16 flex space-x-8 sm:space-x-16 items-center">
                        <ObjectFavoriteButton adId={adId} loginId={loginId} isMobile={isMobile} isPreview={isPreview} />
                        {adId && heading && (
                            <ObjectShareButtons adId={adId} heading={heading} isEnabled={Boolean(status?.isActive && !isPreview)} />
                        )}
                    </div>
                    <div className="mx-0 sm:mx-16 space-y-32">
                        {status && <ObjectStatus status={status} />}
                        <h1 data-testid="adHeading" className="break-words">
                            {heading}
                        </h1>
                        {priceInfo ? (
                            <ObjectPricing
                                pricing={priceInfo}
                                minimumOneWeekRental={priceInfo.minimumOneWeekRental}
                                firstColumnList={objectPricingColumnData}
                            />
                        ) : (
                            newPricing && <ObjectPricing pricing={newPricing} firstColumnList={objectPricingColumnData} />
                        )}
                        <ObjectInfo />
                    </div>

                    {residenceDescription && (
                        <ObjectPanel title="Om boligen" data-testid="residence-title">
                            <ObjectDescription description={residenceDescription} data-testid="residence-description" />
                        </ObjectPanel>
                    )}

                    {!residenceDescription && description && (
                        <ObjectPanel title="Beskrivelse" data-testid="description-title">
                            <ObjectDescription description={description} data-testid="description" />
                        </ObjectPanel>
                    )}

                    {roomsDescription && (
                        <ObjectPanel title="Beskrivelse av rom" data-testid="rooms-title">
                            <ObjectDescription description={roomsDescription} data-testid="room-description" />
                        </ObjectPanel>
                    )}

                    <ObjectPanel title="Fasiliteter">
                        <ObjectInfoList list={facilities} split />
                    </ObjectPanel>
                    <ObjectPanel title="I nærheten">
                        <ObjectInfoList list={poiNearBy} />
                    </ObjectPanel>
                    <ObjectPanel title="Beliggenhet" data-testid="area-title">
                        {areaDescription && <ObjectDescription description={areaDescription} data-testid="area-description" />}
                        <ObjectLocation adId={adId} location={location} isPreview={isPreview} />
                    </ObjectPanel>

                    {isMobile && <div data-testid="objectPage-mobileProfileAndCalendar">{renderProfileAndCalendar()}</div>}

                    <ObjectAreaProfile adId={adId} />
                    <ObjectFooter adId={adId} heading={heading} isPreview={isPreview} modified={modified} />

                    {!isPreview && (
                        <ObjectPanel title="Vurderinger fra gjester" ref={ratingRef}>
                            <ObjectRating adId={adId} isMobile={isMobile} />
                        </ObjectPanel>
                    )}
                </article>
                {!isMobile && <div data-testid="objectPage-profileAndCalendar">{renderProfileAndCalendar()}</div>}
            </div>
            {recommendationData?.items?.length > 0 && (
                <ObjectPanel title="Andre feriehus og hytter" data-testid="objectPage-recommendations">
                    <ObjectRecommendations items={recommendationData.items} />
                </ObjectPanel>
            )}
        </div>
    );
};

ObjectPage.PreviewBottomSheet = PreviewBottomSheet;

ObjectPage.propTypes = {
    deviceType: PropTypes.object.isRequired,
    loginCookieCsrfToken: PropTypes.string.isRequired,
    loginId: PropTypes.number,
    unleash: PropTypes.object.isRequired,
};
